import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  enableHelena,
  generateReply,
  getAgentEligibility,
  getConversation,
  getHelenaAccess,
  getKbDocContent,
  getSuggestedKb,
  getSuggestedReply,
  getTriageStatus,
  getUsageAnalytics,
  kbKeywordSearch,
  refineReply,
  submitTriageReview,
  suggestPrompts,
  takeControl,
} from "controller/api/helena/helena";
import {
  useInvalidateEligibilityOnTicketFetch,
  useIsTicketNewerThan,
} from "controller/cache";
import type {
  AgentEligibilityData,
  AgentEligibilityInput,
  ConversationTurn,
  GenerateReplyInput,
  GetUsageAnalyticsInput,
  HelenaAccessData,
  KbKeywordSearchInput,
  RefineReplyInput,
  SubmitTriageReviewInput,
  SuggestedKbInput,
  SuggestedReplyInput,
  SuggestedReplyReturn,
  SuggestPromptsInput,
  SuggestPromptsReturn,
  TriageStatusData,
  UsageAnalyticsData,
} from "shared";
import { debugConsole } from "utilz/helpers";
import { v4 as uuidv4 } from "uuid";

export const useGetSuggestedReply = ({
  ticketId,
  enabled = true,
}: SuggestedReplyInput & { enabled?: boolean }) => {
  return useQuery<SuggestedReplyReturn | undefined>({
    queryKey: ["suggestedReply", ticketId],
    queryFn: () => getSuggestedReply({ ticketId }),
    onError: (err) => debugConsole(err),
    // staleTime: Infinity,
    // cacheTime: Infinity,
    enabled: enabled && !!ticketId,
  });
};

export const useRefineReply = () => {
  return useMutation((params: RefineReplyInput) => refineReply(params), {
    onError: (err) => debugConsole(err),
  });
};

// ─── Compose Conversation ───────────────────────────────────────────

export const conversationKeys = {
  detail: (ticketId: string) => ["helenaConversation", ticketId] as const,
};

export const useGetConversation = ({
  ticketId,
  enabled = true,
}: {
  ticketId: string;
  enabled?: boolean;
}) => {
  return useQuery<ConversationTurn[]>({
    queryKey: conversationKeys.detail(ticketId),
    queryFn: () => getConversation({ ticketId }),
    onError: (err) => debugConsole(err),
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: enabled && !!ticketId,
  });
};

export const useGenerateReply = () => {
  const queryClient = useQueryClient();
  return useMutation((params: GenerateReplyInput) => generateReply(params), {
    onSuccess: (data, vars) => {
      // Unrelated messages return an empty AI turn — append only the user's
      // turn so the prompt still shows in the conversation, but skip the AI turn.
      if (data?.is_unrelated) {
        queryClient.setQueryData<ConversationTurn[]>(
          conversationKeys.detail(vars.ticketId),
          (prev = []) => [
            ...prev,
            {
              turn_id: uuidv4(),
              role: "user",
              content: vars.prompt ?? "",
              created_at: data.turn?.created_at ?? new Date().toISOString(),
            },
          ],
        );
        return;
      }
      if (!data?.turn) return;
      // Refine calls carry no user prompt — they rewrite the existing reply,
      // so replace the last AI turn in place instead of appending a new card.
      const isRefine = !!vars.refineType && !vars.prompt;

      queryClient.setQueryData<ConversationTurn[]>(
        conversationKeys.detail(vars.ticketId),
        (prev = []) => {
          if (isRefine) {
            const lastAiIdx = prev.findLastIndex((turn) => turn.role === "ai");
            if (lastAiIdx === -1) return [...prev, data.turn];
            const next = [...prev];
            next[lastAiIdx] = data.turn;
            return next;
          }

          // Prompt path: optimistically append the user turn + the AI turn.
          const newTurns: ConversationTurn[] = [
            {
              turn_id: uuidv4(),
              role: "user",
              content: vars.prompt ?? "",
              created_at: data.turn.created_at,
            },
            data.turn,
          ];
          const seen = new Set(prev.map((turn) => turn.turn_id));
          return [
            ...prev,
            ...newTurns.filter((turn) => !seen.has(turn.turn_id)),
          ];
        },
      );
    },
    onError: (err) => debugConsole(err),
  });
};

/**
 * Fetches Helena's quick-action prompt suggestions for the compose flow.
 * Keyed on the ticket so suggestions are computed once per ticket; the
 * ticket context (title/description/history) is passed to the POST body.
 */
export const useSuggestPrompts = ({
  ticketId,
  ticketTitle,
  ticketDescription,
  existingReply,
  conversationHistory,
  enabled = true,
}: SuggestPromptsInput & { ticketId: string; enabled?: boolean }) => {
  return useQuery<SuggestPromptsReturn | undefined>({
    queryKey: ["helenaSuggestPrompts", ticketId],
    queryFn: () =>
      suggestPrompts({
        ticketTitle,
        ticketDescription,
        existingReply,
        conversationHistory,
      }),
    onError: (err) => debugConsole(err),
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: enabled && !!ticketId && !!ticketTitle,
  });
};

export const useGetSuggestedKb = (
  params: SuggestedKbInput & { serviceId: string; enabled: boolean },
) => {
  return useQuery({
    retry: 1,
    queryKey: ["helenaSuggestedKb", params.serviceId],
    queryFn: () => getSuggestedKb(params),
    onError: (err) => debugConsole(err),
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: params.enabled && !!params.summary,
  });
};

export const useKbKeywordSearch = () => {
  return useMutation(
    (params: KbKeywordSearchInput) => kbKeywordSearch(params),
    {
      onSuccess: () => {},
      onError: (err) => debugConsole(err),
    },
  );
};

export const useGetKbDocContent = () => {
  return useMutation((params: { docId: string }) => getKbDocContent(params), {
    onSuccess: () => {},
    onError: (err) => debugConsole(err),
  });
};

// ─── Triage Status ──────────────────────────────────────────────────

export const useGetTriageStatus = ({
  ticketId,
  mspId,
  enabled = true,
}: {
  ticketId: string;
  mspId: string;
  enabled?: boolean;
}) => {
  return useQuery<TriageStatusData | undefined>({
    queryKey: ["triageStatus", ticketId, mspId],
    queryFn: () => getTriageStatus({ ticketId, mspId }),
    enabled: enabled && !!ticketId && !!mspId,
    // MQTT keeps the cache live, so the snapshot never goes stale on a timer —
    // but it is fire-and-forget, so anything published while the tab was closed
    // is gone. Re-reading the source of truth on mount is the only way back in
    // sync; the reconnect case is handled in useTriageStatusMqtt.
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnMount: "always",
  });
};

export const useAgentEligibility = ({
  mspId,
  ticketId,
  enabled = true,
}: AgentEligibilityInput & { enabled?: boolean }) => {
  const isTicketNewerThan = useIsTicketNewerThan();
  const isEnabled = enabled && !!mspId && !!ticketId;

  useInvalidateEligibilityOnTicketFetch({
    mspId,
    ticketId,
    enabled: isEnabled,
  });

  return useQuery<AgentEligibilityData | undefined>({
    queryKey: ["agentEligibility", mspId, ticketId],
    queryFn: () => getAgentEligibility({ mspId, ticketId }),
    enabled: isEnabled,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnMount: (eligibilityQuery) =>
      isTicketNewerThan(ticketId, eligibilityQuery.state.dataUpdatedAt)
        ? "always"
        : false,
    onError: (err) => debugConsole(err),
  });
};

export const useTakeControl = () => {
  return useMutation(
    (params: { ticketId: string; mspId: string }) => takeControl(params),
    {
      onError: (error) => debugConsole(error),
    },
  );
};

// ─── Manual Review Submit ───────────────────────────────────────────

/**
 * Submits the technician's decision on a held phase. On success the pending
 * proposal is cleared from the cached snapshot straight away so the card
 * disappears without waiting on the MQTT echo (`review_applied` for an accept,
 * `review_ignored` for an ignore), which then confirms it and carries the next
 * phase state.
 *
 * An ignore does not resume anything — the run is over — so the optimistic
 * status has to follow the server's verdict rather than assume the chain moved
 * on. If the MQTT echo is lost, this is the state the tab keeps.
 *
 * An ask_user clears the hold too, but the run is very much alive: the
 * clarification loop re-runs asynchronously and the ticket either proceeds on
 * its own or comes back with a second review. It reports `resumed: false`
 * because the phase chain did not resume here, which must NOT be read as an
 * ignore — hence the explicit action check before that fallback.
 */
export const useSubmitTriageReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SubmitTriageReviewInput) => submitTriageReview(input),
    onSuccess: (data, variables) => {
      const isAskUser =
        variables.action === "ask_user" || data?.action === "asked_user";
      const isIgnored =
        !isAskUser &&
        (data?.action === "ignored" ||
          variables.action === "ignore" ||
          data?.resumed === false);

      queryClient.setQueryData<TriageStatusData | undefined>(
        ["triageStatus", variables.ticketId, variables.mspId],
        (prev) => {
          if (!prev) return prev;
          // The MQTT echo routinely beats this HTTP response back, and it
          // carries the run's real state — which may already have moved past
          // "in_progress" to "completed" when the resumed phase was the last
          // one. A cleared hold is how we know the echo landed, so leave that
          // newer snapshot alone rather than rewinding it to a status the run
          // has already left.
          if (!prev.pending_review) return prev;

          return {
            ...prev,
            overall_status: isIgnored ? "ignored" : "in_progress",
            pending_review: null,
          };
        },
      );
    },
    onError: (error) => debugConsole(error),
  });
};

// ─── Usage Analytics ────────────────────────────────────────────────

/**
 * Powers the agent dashboard's metric cards.
 *
 * "Current" ranges (today / this_week / this_month / last_3_months) end at the
 * request moment, so the answer legitimately moves with the clock — the cache
 * must not outlive the page. Hence `cacheTime: 0` and a refetch on mount.
 * Closed ranges are cached the same way for simplicity; they are cheap.
 *
 * No polling: this is a manually-refreshed dashboard, not a live feed.
 */
export const useUsageAnalytics = ({
  range,
  timezone,
  agentId,
  enabled = true,
}: GetUsageAnalyticsInput & { enabled?: boolean }) => {
  return useQuery<UsageAnalyticsData>({
    // `agentId` is part of the key: the same range scoped to a different agent
    // is a different answer, and omitting it would serve one agent's numbers
    // under another agent's name.
    queryKey: ["usageAnalytics", range, timezone ?? "UTC", agentId ?? "all"],
    queryFn: () => getUsageAnalytics({ range, timezone, agentId }),
    enabled,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: "always",
    onError: (err) => debugConsole(err),
  });
};

const HELENA_ACCESS_QUERY_KEY = ["helenaAccess"];

export const useGetHelenaAccess = ({ enabled = true } = {}) => {
  return useQuery<HelenaAccessData | undefined>({
    queryKey: HELENA_ACCESS_QUERY_KEY,
    queryFn: () => getHelenaAccess(),
    onError: (err) => debugConsole(err),
    staleTime: Infinity,
    enabled,
  });
};

export const useEnableHelena = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => enableHelena(),
    onSuccess: (data) => {
      queryClient.setQueryData<HelenaAccessData | undefined>(
        HELENA_ACCESS_QUERY_KEY,
        (prev) =>
          data
            ? {
                created_at: null,
                ...prev,
                ...data,
              }
            : prev,
      );
      queryClient.setQueriesData<{ helena_enabled?: boolean } | undefined>(
        { queryKey: ["msp"] },
        (prev) => (prev ? { ...prev, helena_enabled: true } : prev),
      );
    },
    onError: (err) => debugConsole(err),
  });
};
